/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom"; 
import { databases } from "./appwrite";
import { ID, Query } from "appwrite";
import { User, Mail, AlertTriangle, CheckCircle, Search } from "lucide-react";

// Configuration
const DB_ID = process.env.REACT_APP_APPWRITE_DB_ID || "regdb";
const REG_COL = "692a9f82002dabe4ac9c"; 
const ATT_COL = process.env.REACT_APP_APPWRITE_ATT_COL_ID || "regdb"; 

// 🔥 Updated Session Windows (Based on your latest code)
const SESSION_WINDOWS = {
  1: { start: "09:30", end: "11:15" },
  2: { start: "11:15", end: "13:45" },
  3: { start: "13:45", end: "15:30" },
  4: { start: "15:30", end: "17:00" },
};

const toMinutes = (t) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

export default function AttendancePage() {
  const [searchParams] = useSearchParams();
  const session = searchParams.get("session");
  const urlDate = searchParams.get("date");

  const [identifier, setIdentifier] = useState("");
  const [status, setStatus] = useState("idle"); 
  const [msg, setMsg] = useState("");

  const today = new Date();
  const offset = today.getTimezoneOffset(); 
  const todayISO = new Date(today.getTime() - (offset*60*1000)).toISOString().split('T')[0];

  useEffect(() => {
    if (!session || !["1", "2", "3", "4"].includes(session)) {
      setStatus("error");
      setMsg("Invalid QR Code. Please scan a valid session code.");
    } else if (urlDate && urlDate !== todayISO) {
      setStatus("error");
      setMsg("This QR code is for a different date.");
    }
  }, [session, urlDate, todayISO]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");

    try {
      // 1. Validation
      if (!session) throw new Error("Invalid Session Code");
      if (urlDate && urlDate !== todayISO) throw new Error("QR Expired. Refresh required.");

      // 2. Time Check
      const now = new Date();
      const nowMin = now.getHours() * 60 + now.getMinutes();
      const win = SESSION_WINDOWS[session];
      
      // Strict Check with 15 min buffer
      if (nowMin < toMinutes(win.start) - 15 || nowMin > toMinutes(win.end) + 15) {
         throw new Error(`Session ${session} is closed. Active: ${win.start} - ${win.end}`);
      }

      // 3. Search Logic (Case Insensitive Fix)
      let searchKey = "";
      let searchValue = "";
      const isEmail = identifier.includes("@");

      if (isEmail) {
        searchKey = "email";
        // Emails are always lowercase
        searchValue = identifier.toLowerCase().trim();
      } else {
        searchKey = "name";
        // 🔥 FIX: Convert input to UPPERCASE to match Database format
        searchValue = identifier.toUpperCase().trim(); 
      }

      // 4. Perform Search
      const search = await databases.listDocuments(DB_ID, REG_COL, [
        Query.equal(searchKey, searchValue),
      ]);

      if (search.total === 0) {
        throw new Error(
            isEmail 
            ? "Email not found. Check spelling." 
            : `Name "${identifier}" not found. Try searching by Email.`
        );
      }

      if (!isEmail && search.total > 1) {
        throw new Error(`Multiple people found with name "${identifier}". Please use your Email.`);
      }

      const regData = search.documents[0];

      // 5. Duplicate Check
      const todayString = new Date().toLocaleDateString("en-IN");
      const dup = await databases.listDocuments(DB_ID, ATT_COL, [
        Query.equal("email", regData.email),
        Query.equal("day", todayString), 
        Query.equal("session", session),
      ]);

      if (dup.total > 0) throw new Error("Attendance already marked for this session.");

      // 6. Mark Attendance
      await databases.createDocument(DB_ID, ATT_COL, ID.unique(), {
        name: regData.name,
        email: regData.email,
        session: session,
        day: todayString,
        timestamp: new Date().toISOString(),
        designation: regData.designation
      });

      setStatus("success");
      setMsg(`${regData.name}, your attendance is marked!`);
      setIdentifier("");

    } catch (err) {
      console.error(err);
      setStatus("error");
      setMsg(err.message);
    }
  };

  // --- Render Views ---

  if (status === "error" && (msg.includes("Invalid") || msg.includes("closed") || msg.includes("different date"))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-6">
        <div className="bg-white p-8 rounded-2xl shadow-xl text-center max-w-sm w-full border border-gray-200">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600">{msg}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 p-4 font-sans">
      <div className="bg-white shadow-2xl rounded-3xl p-8 w-full max-w-md text-center border border-white/50">
        
        <div className="mb-8">
            <h2 className="text-2xl font-extrabold text-slate-800 tracking-tight">
            Mark Attendance
            </h2>
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-bold mt-3 border border-blue-100">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                </span>
                Session {session || "?"}
            </div>
        </div>

        {status === "success" ? (
            <div className="bg-green-50 border border-green-100 rounded-2xl p-8 mb-4 animate-fadeIn">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="font-bold text-green-800 text-xl mb-2">Success!</h3>
                <p className="text-green-700 font-medium mb-6">{msg}</p>
                <button 
                    onClick={() => setStatus("idle")} 
                    className="text-sm font-semibold text-green-600 hover:text-green-800 underline decoration-2 underline-offset-2"
                >
                    Mark Another Person
                </button>
            </div>
        ) : (
            <form onSubmit={handleSubmit} className="space-y-6 text-left">
              {status === "error" && (
                  <div className="p-4 bg-red-50 text-red-700 text-sm rounded-xl border border-red-100 flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                      <span>{msg}</span>
                  </div>
              )}
              
              <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2 ml-1">
                    Lookup Identity
                  </label>
                  <div className="relative group">
                    <input
                        type="text"
                        className="w-full pl-11 pr-4 py-4 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all font-medium text-slate-800 placeholder:text-slate-400"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        placeholder="Enter Name OR Email..."
                        required
                    />
                    <div className="absolute left-3.5 top-4 text-slate-400 group-focus-within:text-blue-500 transition-colors">
                        {identifier.includes("@") ? <Mail size={20} /> : <User size={20} />}
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2 ml-1 flex items-center gap-1">
                    <Search className="w-3 h-3" />
                    Enter your name <b>exactly</b> as registered or use email.
                  </p>
              </div>

              <button 
                disabled={status === "loading"}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center gap-2"
              >
                {status === "loading" ? (
                    <>
                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                        Processing...
                    </>
                ) : (
                    "Submit Attendance"
                )}
              </button>
            </form>
        )}
        
        <div className="mt-8 pt-6 border-t border-slate-100">
            <p className="text-xs text-slate-400">
                MMTTC Faculty Development Program • 2026
            </p>
        </div>
      </div>
    </div>
  );
}